import { useCallback, useEffect, useState } from 'react';
import { GamePlayer } from './components/GamePlayer';
import { Portal } from './components/Portal';
import { GAMES } from './games/catalog';
import { arcadeAudio } from './platform/audio';
import { grantStamps } from './platform/stamps';
import { writeBest } from './platform/storage';
import type { GameId, GameResult } from './platform/types';

export default function App() {
  const [active, setActive] = useState<GameId | null>(null);
  const [hashTick, setHashTick] = useState(0);

  useEffect(() => {
    const unlock = () => {
      void arcadeAudio.unlock();
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    const onHash = () => {
      const id = location.hash.replace('#/', '') as GameId;
      if (GAMES.some((g) => g.id === id)) setActive(id);
      else setActive(null);
      setHashTick((n) => n + 1);
    };
    onHash();
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('hashchange', onHash);
    };
  }, []);

  const open = useCallback((id: GameId) => {
    void arcadeAudio.unlock();
    location.hash = `/${id}`;
    setActive(id);
  }, []);

  const close = useCallback(() => {
    arcadeAudio.stopMusic();
    location.hash = '';
    setActive(null);
  }, []);

  const onResult = useCallback((id: GameId, result: GameResult) => {
    writeBest(id, result.score);
    if (result.stamps) grantStamps(id, result.stamps);
  }, []);

  const game = GAMES.find((g) => g.id === active);

  return (
    <div className="app">
      {game ? (
        <GamePlayer
          key={`${game.id}-${hashTick}`}
          meta={game}
          onExit={close}
          onResult={(r) => onResult(game.id, r)}
        />
      ) : (
        <Portal onPlay={open} />
      )}
    </div>
  );
}
